import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivationStart,
  Event, GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CountryService } from './services/country/country.service';
import { DialingPrefixResponse } from './shared/models/DialingPrefixResponse';
import { GdlService } from './shared/services/gdl-service/gdl.service';
import { HostContextService } from './services/host-context/host-context.service';
import { I18n } from './i18n';
import { LoginInfoService } from './services/login-info/login-info.service';
import { SessionStorageService } from './shared/services/sessionStorage-service/session-storage.service';
import { SharedService } from 'src/app/shared/services/shared-service/shared.service';
import { WindowRef } from './util/window.ref';
import { getPolarisLocaleCode, toIsoLocale } from './util/polaris-locale.adapter';
import {
  HeadFootService, Locale,
  LocaleService,ConfigurationService
} from '@ciam/library';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Component({
  selector: 'user-login-contact-info',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly EXTRACT_LOCALE_REGEX = /^([a-z]{2})[_-]([a-z]{2})/i;
  gdlServerType = 'server';
  gdlContextPostType = 'POST';
  gdlContextGetType = 'GET';
  loading = true;
  private shfLocaleListenerRetryId: number | undefined;
  private readonly localeSyncEvents = [
    'single-spa:routing-event',
    'single-spa:before-routing-event',
    'single-spa:app-change',
    'popstate',
    'hashchange'
  ];
  private readonly localeTokenPattern = /^[a-z]{2}[-_][a-z]{2}$/i;
  private readonly syncLocaleFromNavigation = (): void => {
    this.updateLocaleFromLocation();
    setTimeout(() => this.updateLocaleFromLocation(), 0);
  };

  get isPolaris(): boolean {
    return this.hostContext.isPolaris;
  }

  /**
   * Layout state for the template (see app.component.html):
   *  - `polarisUx`     — inside shell + `usePolarisUx` ON: shell renders header + sidebar,
   *                      the app renders content only (no own sidebar, no header/footer).
   *  - `showHeadFoot`  — standalone/dotcom: render own `ciam-head-foot` + sidebar.
   *  - neither         — inside shell + `usePolarisUx` OFF: own sidebar, no second header.
   * `isReady` gates rendering until the context is resolved to avoid a layout flicker.
   */
  polarisUx = false;
  showHeadFoot = false;
  isReady = false;

  private subscriptions = new Subscription();

  constructor(
    private localeService: LocaleService,
    private titleService: Title,
    private translateService: TranslateService,
    public loginService: LoginInfoService,
    public sharedService: SharedService,
    private configService: ConfigurationService,
    public countryService: CountryService,
    private gdlService: GdlService,
    private headFootService: HeadFootService,
    private router: Router,
    private hostContext: HostContextService,
    private sessionStorageService: SessionStorageService) {
    this.configureLanguages();
    this.handleRouterEvents();
  }

  /**
   * Sequence of events (short-listed for this function)
   * NavigationStart>GuardsCheckStart>ActivationStart>NavigationEnd|NavigationCancel|NavigationError
   */
  handleRouterEvents(): void {
    this.sessionStorageService.setItem('isAppLoading', true);
    this.router.events.subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart:
        case event instanceof ActivationStart: {
          this.loading = true;
          break;
        }
        case event instanceof GuardsCheckStart:
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loading = false;
          this.sessionStorageService.setItem('isAppLoading', this.loading);
          //Emitting hashchange event to let sidebar know about # changes on redirects from /#/ to /#/login
          if (event instanceof NavigationEnd) {
            window.dispatchEvent(new HashChangeEvent('hashchange'));
          }
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  ngOnInit(): void {
    // Resolve the layout context once, up front, so the template can pick a single state.
    const inShell = this.hostContext.isPolaris;
    this.polarisUx = inShell ? this.hostContext.usePolarisUx : false;
    this.showHeadFoot = !inShell;

    //Call base APIs
    this.getUserInfo();
    this.getRoleAndStatus();
    // this.callRecipient();
    this.listenForUserLogoutEvent();
    if (inShell) {
      // POLARIS shell owns the locale: the language switcher lives in the shell
      // header, which the MFE never renders. Consume the platform locale service
      // and seed LocaleService with it so ngx-translate, the locale interceptor
      // and GDL all flow from a single POLARIS-driven source of truth instead of
      // the CIMA head/foot SHF bridge. The shell reloads on language switch, so
      // reading once on mount is sufficient.
      this.applyPolarisLocale();
    } else {
      this.registerShellLocaleListener();
      this.updateLocaleFromLocation();
      this.localeSyncEvents.forEach((eventName) => {
        window.addEventListener(eventName, this.syncLocaleFromNavigation);
      });
    }
    // Remove the 'fxg-main-content' class coming from (ciam-head-foot) from all matching elements after view init, only if it exists
    setTimeout(() => {
      const elements = document.querySelectorAll('.fxg-main-content');
      if (elements.length > 0) {
        elements.forEach((el: Element) => {
          el.classList.remove('fxg-main-content');
        });
      }
    }, 0);

    this.isReady = true;
  }

  ngOnDestroy(): void {
    if (this.shfLocaleListenerRetryId) {
      window.clearTimeout(this.shfLocaleListenerRetryId);
    }
    this.localeSyncEvents.forEach((eventName) => {
      window.removeEventListener(eventName, this.syncLocaleFromNavigation);
    });
    this.subscriptions.unsubscribe();
  }

  /**
   * Called during constructor cycle to load non-angular services i.e. header-footer and locale
   * Initializes locale and languages for the application
   */
  private configureLanguages(): void {
    this.translateService.setDefaultLang('en');
    this.translateService.addLangs(I18n.SUPPORTED_LANGS);
    this.translateService.get('title')
      .subscribe(title => this.titleService.setTitle(title));
    //Note: link all locale related changes here to this subscriber
    this.subscriptions.add(
       this.localeService.watch$().pipe(
         map((locale: Locale) => this.extractLocaleString(locale)),
         map((locale: string) => this.toSupportedLanguage(locale)),
         distinctUntilChanged()
       ).subscribe((language: string) => this.translateService.use(language))
     );
  }

  private applyPolarisLocale(): void {
    getPolarisLocaleCode().then((locale) => {
      // e.g. { languageCode: 'es', countryCode: 'us' } -> 'es-us'.
      // LocaleService.watch$() (wired in configureLanguages) then activates the
      // matching ngx-translate bundle, and solveLocale() feeds the interceptor.
      this.localeService.update(toIsoLocale(locale));
    });
  }

  private toSupportedLanguage(locale?: string | null): string {
    const localeValue = String(locale || 'en_US');
    if (I18n.SUPPORTED_LANGS.includes(localeValue)) {
      return localeValue;
    }

    const match = localeValue.match(AppComponent.EXTRACT_LOCALE_REGEX);
    const language = match?.[1]?.toLowerCase() || 'en';
    const country = match?.[2]?.toUpperCase() || 'US';
    const posix = `${language}_${country}`;
    const includesPosix = I18n.SUPPORTED_LANGS.includes(posix);
    const includesLanguageCode = I18n.SUPPORTED_LANGS.includes(language);

    return includesPosix ? posix : includesLanguageCode ? language : 'en';
  }

  private extractLocaleString(locale?: Locale | null): string {
    if (!locale) {
      return 'en_US';
    }

    return (typeof locale.posix === 'string' && locale.posix)
      || (typeof locale.iso === 'string' && locale.iso)
      || (typeof locale.language === 'string' && locale.language)
      || (typeof locale.smart === 'string' && locale.smart)
      || 'en_US';
  }

  private updateLocaleFromLocation(): void {
    const localeFromLocation = this.extractLocaleFromLocation();
    if (localeFromLocation) {
      this.localeService.update(localeFromLocation);
      return;
    }
    this.localeService.update(this.localeService.solveLocale());
  }

  private extractLocaleFromLocation(): string | null {
    const localeParam = new URLSearchParams(window.location.search).get('locale');
    if (localeParam && this.localeTokenPattern.test(localeParam)) {
      return localeParam.replace('_', '-').toLowerCase();
    }

    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const localeSegment = pathSegments.find((segment) => this.localeTokenPattern.test(segment));
    if (localeSegment) {
      return localeSegment.replace('_', '-').toLowerCase();
    }

    return null;
  }

  private registerShellLocaleListener(retry = 0): void {
    const shfOnLocaleChange = (window as unknown as {
      SHF_onLocaleChange?: (callback: (locale: { SHFlocale?: string } | string) => void) => void
    }).SHF_onLocaleChange;

    if (typeof shfOnLocaleChange === 'function') {
      shfOnLocaleChange((locale) => {
        const localeValue = typeof locale === 'string' ? locale : locale?.SHFlocale;
         if (localeValue) {
           this.localeService.update(localeValue);
        }
      });
      return;
    }

    if (retry < 20) {
      this.shfLocaleListenerRetryId = window.setTimeout(() => {
        this.registerShellLocaleListener(retry + 1);
      }, 500);
    }
  }

  // todo fix interpolate
  listenForUserLogoutEvent(): void {
    // Polaris shell handles authentication - skip logout listener in MFE mode
    if (this.isPolaris) {
      return;
    }

    if(!window.location.href.includes('mock=true') && !this.configService.select('env.mock')) {
      let homePageUrl = `https://${this.configService.select<string>('env.host')}${this.configService.select<string>('links.homePageUrl')}`;
      this.localeService.interpolate(homePageUrl).then((value) => {
        homePageUrl = value;
      });
      this.subscriptions.add(
        this.headFootService.onAnonymous$().subscribe(() => {
          this.openUrl(homePageUrl);
        })
      );
    }
  }

  openUrl(url: string): void {
    WindowRef.nativeWindow.location.href = url;
  }

  /**
   * Calls user info API. Response is stored in SharedService
   * refactor [high:5:20230807] refactor shared service logic
   */
  getUserInfo(): void {
    this.subscriptions.add(
      this.loginService.getLoginInfo().subscribe((response) => {
        this.sharedService.apiResponse = response;
        const userProfile = response?.output?.userProfile;
        if (!userProfile) {
          this.sharedService.phoneCode = '';
          this.sharedService.isProfileLocked = false;
          return;
        }
        this.sharedService.isProfileLocked = userProfile.profileLocked ?? false;
        const countryCode = userProfile.userProfileAddress?.address?.countryCode;
        if (countryCode) {
          this.getCountryPhoneCode(countryCode.toUpperCase());
        }
        this.sessionStorageService.setItem('companyName', userProfile.userProfileAddress?.contact?.companyName);
      }, (httpError) => {
        this.sharedService.apiResponse = {};
        this.sharedService.phoneCode = '';
        this.sharedService.isProfileLocked = false;
        // GDL will triggers in case of server validation error
        this.gdlService.captureGdlErrorMessages(this.gdlServerType, httpError, this.gdlContextGetType);
      })
    );
  }

  /**
   * Calls role and status API. Response is stored in SharedService
   * refactor [high:5:20230807] refactor shared service logic
   */
  getRoleAndStatus(): any {
    this.loginService.getRoleAndStatus().subscribe((response) => {
      this.sharedService.apiRoleResponse = response;
    }, (httpError) => {
    });
  }

  /**
   * Calls dialing codes. Response is stored in SharedService
   * refactor [high:5:20230807] refactor shared service logic
   */
  getCountryPhoneCode(profileCountryCode: string): void {
    this.countryService.getDialingPrefixList().subscribe((response: DialingPrefixResponse) => {
      this.sharedService.phoneCode = response.output.countryPrefix.filter(country => country.countryCode === profileCountryCode)[0].countryDialingCode;
    }, (httpError) => {
      this.sharedService.phoneCode = '';
      // GDL will triggers in case of server validation error
      this.gdlService.captureGdlErrorMessages(this.gdlServerType, httpError, this.gdlContextPostType);
    });
  }
}

